import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import { 
  Search, Monitor, Power, PowerOff, Pause, Link2,
  ChevronLeft, ChevronRight, Filter, X, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const VirtVMs = () => {
  const [loading, setLoading] = useState(true);
  const [vms, setVMs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', power_state: '', has_server: '' });
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  
  // Active stat filter (for visual feedback on cards)
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const fetchVMs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.power_state) params.append('power_state', filters.power_state);
      if (filters.has_server === 'yes') params.append('has_server', 'true');
      if (filters.has_server === 'no') params.append('has_server', 'false');

      const res = await api.get(`/virt/vms?${params}`);
      setVMs(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar VMs');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/virt/vms/stats');
      setStats(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchVMs, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  // Sort handler
  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort icon component
  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  // Stat card click handler - filters the table
  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    switch (filterType) {
      case 'powered_on':
        setFilters({ search: '', power_state: 'poweredOn', has_server: '' });
        break;
      case 'powered_off':
        setFilters({ search: '', power_state: 'poweredOff', has_server: '' });
        break;
      case 'with_server':
        setFilters({ search: '', power_state: '', has_server: 'yes' });
        break;
      case 'without_server':
        setFilters({ search: '', power_state: '', has_server: 'no' });
        break;
      default:
        setFilters({ search: '', power_state: '', has_server: '' });
        break;
    }
  };

  const getPowerIcon = (state) => {
    switch (state) {
      case 'poweredOn': return <Power className="h-4 w-4 text-green-500" />;
      case 'poweredOff': return <PowerOff className="h-4 w-4 text-red-500" />;
      case 'suspended': return <Pause className="h-4 w-4 text-yellow-500" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };

  const getPowerLabel = (state) => {
    switch (state) {
      case 'poweredOn': return 'Encendida';
      case 'poweredOff': return 'Apagada';
      case 'suspended': return 'Suspendida';
      default: return state || '-';
    }
  };

  const getToolsBadge = (status) => {
    if (!status) return <span className="text-muted-foreground">-</span>;
    if (status === 'toolsOk' || status === 'guestToolsRunning') {
      return <Badge variant="outline" className="text-green-500 border-green-500">OK</Badge>;
    } else if (status === 'toolsOld') {
      return <Badge variant="outline" className="text-yellow-500 border-yellow-500">Antiguo</Badge>;
    } else if (status === 'toolsNotInstalled') {
      return <Badge variant="outline" className="text-red-500 border-red-500">Sin instalar</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  const clearFilters = () => {
    setFilters({ search: '', power_state: '', has_server: '' });
    setActiveStatFilter('all');
  };

  const hasFilters = filters.search || filters.power_state || filters.has_server;

  const formatSize = (gb) => {
    if (!gb) return '-';
    const num = Number(gb);
    if (num >= 1024) return `${(num / 1024).toFixed(1)} TB`;
    return `${num.toFixed(0)} GB`;
  };

  // Sortable header component
  const SortableHeader = ({ field, children, className = '' }) => (
    <th 
      className={`cursor-pointer hover:bg-muted/70 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <SortIcon field={field} />
      </div>
    </th>
  );

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Máquinas Virtuales</h1>
          <p className="page-subtitle">Inventario de VMs con relación a servidores físicos</p>
        </div>

        {/* Stats - Clickeable Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`}
              onClick={() => handleStatFilter('all')}
              data-testid="stat-total"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{stats.total || 0}</p>
                <p className="stats-card-label">Total VMs</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'powered_on' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`}
              onClick={() => handleStatFilter('powered_on')}
              data-testid="stat-powered-on"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-green-500">{stats.powered_on || 0}</p>
                <p className="stats-card-label">Encendidas</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'powered_off' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`}
              onClick={() => handleStatFilter('powered_off')}
              data-testid="stat-powered-off"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-red-500">{stats.powered_off || 0}</p>
                <p className="stats-card-label">Apagadas</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-blue-500/50 ${activeStatFilter === 'with_server' ? 'border-blue-500 ring-1 ring-blue-500/20' : ''}`}
              onClick={() => handleStatFilter('with_server')}
              data-testid="stat-with-server"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-blue-500">{stats.with_server || 0}</p>
                <p className="stats-card-label">Con Servidor</p>
              </CardContent>
            </Card>
            <Card 
              className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'without_server' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`}
              onClick={() => handleStatFilter('without_server')}
              data-testid="stat-without-server"
            >
              <CardContent className="p-4 text-center">
                <p className="stats-card-value text-yellow-500">{stats.without_server || 0}</p>
                <p className="stats-card-label">Sin Servidor</p>
              </CardContent>
            </Card>
            <Card className="card-subtle">
              <CardContent className="p-4 text-center">
                <p className="stats-card-value">{formatSize(stats.total_ram_gb)}</p>
                <p className="stats-card-label">RAM Total</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Filtros:</span>
              </div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar VM, IP, servidor..."
                  value={filters.search}
                  onChange={(e) => {
                    setFilters(prev => ({ ...prev, search: e.target.value }));
                    setActiveStatFilter('');
                  }}
                  className="h-9"
                  data-testid="vm-search-input"
                />
              </div>
              <Select 
                value={filters.power_state || "todos"} 
                onValueChange={(v) => {
                  setFilters(prev => ({ ...prev, power_state: v === "todos" ? "" : v }));
                  setActiveStatFilter('');
                }}
              >
                <SelectTrigger className="w-36 h-9" data-testid="vm-power-filter">
                  <SelectValue placeholder="Estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="poweredOn">Encendidas</SelectItem>
                  <SelectItem value="poweredOff">Apagadas</SelectItem>
                  <SelectItem value="suspended">Suspendidas</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={filters.has_server || "todos"} 
                onValueChange={(v) => {
                  setFilters(prev => ({ ...prev, has_server: v === "todos" ? "" : v }));
                  setActiveStatFilter('');
                }}
              >
                <SelectTrigger className="w-40 h-9" data-testid="vm-server-filter">
                  <SelectValue placeholder="Servidor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="yes">Con servidor</SelectItem>
                  <SelectItem value="no">Sin servidor</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" /> Limpiar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="vms-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="power_state" className="text-center justify-center">ESTADO</SortableHeader>
                  <SortableHeader field="vcpu" className="text-center justify-center">vCPU</SortableHeader>
                  <SortableHeader field="ram_gb" className="text-center justify-center">RAM</SortableHeader>
                  <SortableHeader field="disk_total_gb" className="text-center justify-center">DISCO</SortableHeader>
                  <SortableHeader field="ip_address" className="text-left">IP</SortableHeader>
                  <SortableHeader field="esxi_host" className="text-left">HOST ESXi</SortableHeader>
                  <SortableHeader field="server_hostname" className="text-left">SERVIDOR</SortableHeader>
                  <th>TOOLS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : vms.length === 0 ? (
                  <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">No hay VMs registradas</td></tr>
                ) : vms.map(vm => (
                  <tr key={vm.id} className="border-b hover:bg-muted/30" data-testid={`vm-row-${vm.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <span className="font-medium">{vm.name}</span>
                          {vm.is_template && (
                            <Badge variant="secondary" className="ml-2 text-xs">Template</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground ml-6">{vm.guest_os || '-'}</p>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {getPowerIcon(vm.power_state)}
                        <span className="text-xs">{getPowerLabel(vm.power_state)}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">{vm.vcpu || '-'}</td>
                    <td className="p-4 text-center">{vm.ram_gb ? `${Number(vm.ram_gb).toFixed(0)} GB` : '-'}</td>
                    <td className="p-4 text-center">{formatSize(vm.disk_total_gb)}</td>
                    <td className="p-4 font-mono text-xs">{vm.ip_address || '-'}</td>
                    <td className="p-4">
                      <div>
                        <span className="text-muted-foreground text-sm">{vm.esxi_host || '-'}</span>
                        {vm.cluster_name && (
                          <p className="text-xs text-muted-foreground">{vm.cluster_name}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      {vm.server_id ? (
                        <Link 
                          to={`/assets/servers/${vm.server_hostname}`} 
                          className="flex items-center gap-1 text-blue-500 hover:underline text-sm"
                        >
                          <Link2 className="h-3 w-3" />
                          {vm.server_hostname}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-4">
                      {getToolsBadge(vm.tools_status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === 1}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {pagination.page} de {pagination.pages} ({pagination.total} VMs)
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              disabled={pagination.page === pagination.pages}
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default VirtVMs;
